/**
 * The clearance run's overlay card — the clearance twin of
 * `DeviationVolumePanel` / `SurfaceDifferencePanel`, drawn on the same
 * `DockPanel` chrome so the analysis column reads as one instrument whichever
 * analysis produced the layer.
 *
 * Its first job is to be the way into `ClearanceDock` — the `clearance-chip`
 * button is the ONLY writer of `dataset: 'clearance'`, so if a reader cannot
 * tell it is clickable the whole feature is unreachable. The owner reported
 * exactly that about the first card (a bordered card-in-card nobody read as a
 * button), so the entry is said twice: a header icon toggle that survives a
 * collapse, and a slim full-width "Open results" button closing the body. The
 * R3 audit (B4) wanted the body button gone; the owner overruled it — the
 * header icon alone is the affordance he missed in the first place.
 *
 * Reading order in the body is verdict → figures → worst point → reference
 * → entry (audit A7: the reference block used to take ~45 % of the card by
 * default). Each fact is stated ONCE: the reason line beside the verdict adds
 * what the KPIs do not (how many station intervals are affected, or the
 * tightest margin), the worst-point line adds only what the KPIs lack (offset
 * and height), and the run's parameters sit in a "Run details" disclosure,
 * closed by default. The violation share and the scored-point count live in
 * the dock's Details popover.
 *
 * Everything is read from the layer's metadata summary
 * (`DerivedClearanceLayerMetadata.clearance`), ~1.5 KB that rides on the
 * layers query anyway; the per-bin JSON is the dock's business and is not
 * fetched for this card.
 *
 * The header is `DockPanelActions` with `kind='clearance'` — the same report
 * button the two sibling cards carry, here asking for the whole-run summary
 * PDF (`CLEARANCE`; clearance report spec §5.1) — with the dock toggle in its
 * `children` slot, so the header reads: name, report, dock.
 */
import { useState } from 'react';

import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily, atomWithStorage, createJSONStorage } from 'jotai/utils';
import { ChevronDown, ChevronRight, PanelBottomClose, PanelBottomOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { alignmentWalkAtom } from '@/atoms/alignment.atom';
import { clearanceWalkTargetAtom } from '@/atoms/clearanceNavigation.atom';
import {
  stationClipToBinAtom,
  stationVolumeDockAtom,
  stationVolumeDockHeightAtom,
  stationVolumeSelectionAtom,
} from '@/atoms/stationVolumes.atom';
import { Button } from '@/components/ui/button';
import { VerdictBadge, VerdictChip } from '@/components/VerdictChip';
import { DOCK_PAD, DockKpi, DockPanel } from '@/components/dock/DockHeaderParts';
import { DOCK_HEADER_ICON_BUTTON, DockPanelActions } from '@/components/dock/DockPanelActions';
import { CLEARANCE_FILL } from '@/lib/clearance/chart';
import type { ClearanceLayerMeta } from '@/lib/clearance/types';
import { useClearanceVerdict } from '@/lib/clearance/useClearanceVerdict';
import { formatFixed, formatSigned, staOnly } from '@/lib/stationVolumes/format';
import { SV_TOKENS } from '@/lib/stationVolumes/tokens';

type PanelState = 'collapsed' | 'expanded';

// localStorage and default `expanded`, exactly as the two sibling cards in the
// column persist theirs — a collapse survives a browser restart the same way
// on all three, and a card that defaulted shut would hide its verdict next to
// neighbours showing their figures.
const storage = createJSONStorage<PanelState>(() => localStorage);
const clearancePanelStateAtom = atomFamily((layerId: string) =>
  atomWithStorage<PanelState>(`@actegon.gis.clearance.panel.${layerId}`, 'expanded', storage),
);

// "Is THIS layer's clearance dock open?" as a boolean atom, not a read of
// `stationVolumeDockAtom` itself: that one carries the live height, which the
// resize grip rewrites on every pointermove, and the card has no reason to
// re-render through a drag.
const clearanceDockOpenAtom = atomFamily((layerId: string) =>
  atom((get) => {
    const dock = get(stationVolumeDockAtom);
    return dock?.layerId === layerId && dock.dataset === 'clearance';
  }),
);

export interface ClearancePanelProps {
  /** The project the summary PDF is filed under (the header's report button). */
  projectId: string;
  layerId: string;
  layerName: string;
  clearance: Pick<ClearanceLayerMeta, 'summary' | 'params' | 'profile' | 'alignment'>;
}

const metres = (v: number | null | undefined, decimals: number): string =>
  v == null || !Number.isFinite(v) ? '—' : `${formatFixed(v, decimals)} m`;

const signedMetres = (v: number | null | undefined, decimals: number): string =>
  v == null || !Number.isFinite(v) ? '—' : `${formatSigned(v, decimals)} m`;

// The bare figures for a sentence that carries its own units.
const formatFixedOrDash = (v: number | null | undefined, decimals: number): string =>
  v == null || !Number.isFinite(v) ? '—' : formatFixed(v, decimals);

const formatSignedOrDash = (v: number | null | undefined, decimals: number): string =>
  v == null || !Number.isFinite(v) ? '—' : formatSigned(v, decimals);

export function ClearancePanel({ projectId, layerId, layerName, clearance }: ClearancePanelProps) {
  const { t } = useTranslation('project');
  const [state, setState] = useAtom(clearancePanelStateAtom(layerId));
  const setDock = useSetAtom(stationVolumeDockAtom);
  const setSelection = useSetAtom(stationVolumeSelectionAtom);
  const setClipToBin = useSetAtom(stationClipToBinAtom(layerId));
  const setWalk = useSetAtom(alignmentWalkAtom);
  const setWalkTarget = useSetAtom(clearanceWalkTargetAtom);
  const dockOpen = useAtomValue(clearanceDockOpenAtom(layerId));
  // Seed the dock height from the persisted preference, never from the
  // default — opening the dock must not stomp the user's drag (ruling A7).
  const dockHeight = useAtomValue(stationVolumeDockHeightAtom);

  const { summary, params, profile, alignment } = clearance;
  const isExpanded = state === 'expanded';
  const { verdict, rows, rule, band, insidePct, bandPct } = useClearanceVerdict(summary, params.nearMissBandsM);
  // Points inside, whatever the verdict: under the share thresholds a
  // MARGINAL run can have some, and the reader still needs where they are.
  const violating = summary.violationCount > 0;
  // 3 dp wherever this card prints the depth, the dock's KPI precision: the
  // collapsed line and the KPI are the same figure and must be the same string.
  const worst = summary.maxIntrusionM == null ? '—' : formatFixed(summary.maxIntrusionM, 3);
  const margin = summary.minMarginM == null ? null : formatFixed(summary.minMarginM, 3);
  // `staOnly`, not `staTag`: the tile's label already says "At station", and
  // "STA 12+371.00" broke the tile onto two lines (audit A7).
  const worstStation = summary.worstStationDisplayM == null ? '—' : staOnly(summary.worstStationDisplayM, 2);
  // Reference material, closed by default and not remembered: it is looked
  // up, not monitored.
  const [detailsOpen, setDetailsOpen] = useState(false);

  const openDock = () => setDock({ layerId, tab: 'chart', height: dockHeight, dataset: 'clearance' });
  // The same three writes as the dock's own close button: the brush and the
  // clip-to-station slab are both shared viewer state, and reopening must ask
  // for the slab again rather than silently re-slicing the cloud (ruling D36).
  const closeDock = () => {
    setDock(null);
    setSelection(null);
    setClipToBin(false);
  };
  const toggleDock = () => (dockOpen ? closeDock() : openDock());

  // Click walks, never brushes (round-2 decision 3): "Go to worst" drops any
  // brush, which would otherwise narrow every KPI to one bin, and lands the
  // walk on the worker's exact worst station.
  //
  // A walk already on THIS run's alignment moves at once. Every other case —
  // no walk yet (the dock's walk-start would seed the run's FIRST station), a
  // walk on another alignment, a run whose alignment is not loaded — is the
  // dock's to resolve, so the target is posted for it (`clearanceWalkTargetAtom`)
  // and the dock consumes it once it has the run. An already-open dock keeps
  // the tab the reader is on.
  const goToWorst = () => {
    if (!dockOpen) openDock();
    setSelection((s) => (s?.layerId === layerId ? null : s));
    const station = summary.worstStationInternalM;
    if (station == null) return;
    setWalk((w) => (w && w.key === alignment.key ? { ...w, station } : w));
    setWalkTarget({ layerId, station });
  };

  // The one-line reason beside the verdict. It says what the KPIs below do
  // NOT: on a FAIL how much of the run is affected (the count, depth and
  // station are the KPIs' job), otherwise the two shares the verdict is
  // judged on (inside, and within the outermost band). A clean run is a
  // SENTENCE (finding I12), never a row of zeros.
  const reason = (() => {
    switch (verdict) {
      case 'FAIL':
        // Under the share thresholds a run can FAIL on its band alone; then
        // "0 of N intervals affected" would be false — the shares say why.
        if (summary.violationCount === 0)
          return t('clearance.card.reasonMarginal', { inside: insidePct, near: bandPct, band });
        return t('clearance.card.reasonFail', {
          count: summary.binCount,
          formatted: summary.binsWithViolations.toLocaleString(),
          total: summary.binCount.toLocaleString(),
        });
      case 'MARGINAL':
        return t('clearance.card.reasonMarginal', { inside: insidePct, near: bandPct, band });
      case 'INCONCLUSIVE':
        return t('clearance.card.reasonInconclusive');
      default:
        return margin == null ? t('clearance.chip.clear') : t('clearance.card.reasonPass', { margin, near: bandPct, band });
    }
  })();

  // The figure beside the collapsed badge.
  const collapsedFigure = violating
    ? t('clearance.card.collapsed', {
        count: summary.violationCount,
        formatted: summary.violationCount.toLocaleString(),
        worst,
      })
    : verdict !== 'INCONCLUSIVE' && margin != null
      ? t('clearance.card.collapsedMargin', { margin })
      : null;

  const OpenIcon = dockOpen ? PanelBottomClose : PanelBottomOpen;

  return (
    <>
      {/* `.sv-palette` brings in the `--sv-*` variables without a surface of
          its own, so the intrusion red here is the dock's and the chart's. */}
      <style>{SV_TOKENS}</style>
      <DockPanel
        className='sv-palette'
        expanded={isExpanded}
        onToggle={() => setState(isExpanded ? 'collapsed' : 'expanded')}
        toggleLabel={isExpanded ? t('dockPanel.collapse') : t('dockPanel.expand')}
        title={t('clearance.chip.title')}
        summary={
          !isExpanded && (
            // Badge FIRST: the summary truncates (`DockPanel`), and whatever
            // comes last is what the ellipsis eats — the verdict must not be
            // it. The span badge, not `VerdictChip`: this sits inside the
            // collapse toggle, and a button in a button is invalid HTML.
            <>
              <VerdictBadge verdict={verdict} className='ml-1.5 align-middle' />
              {collapsedFigure && (
                <span
                  data-testid='clearance-card-collapsed'
                  className='ml-1.5 text-slate-700 tabular-nums dark:text-stone-200'
                >
                  {collapsedFigure}
                </span>
              )}
            </>
          )
        }
        actions={
          <DockPanelActions projectId={projectId} layerId={layerId} layerName={layerName} kind='clearance'>
            {/* In the header, not only the body, so the dock is one click
                away on a COLLAPSED card too. A toggle: pressed while this
                layer's dock is up, and a second click closes it. */}
            <button
              type='button'
              data-testid='clearance-dock-toggle'
              aria-pressed={dockOpen}
              aria-label={dockOpen ? t('clearance.card.hideHelp') : t('clearance.chip.open')}
              title={dockOpen ? t('clearance.card.hideHelp') : t('clearance.chip.open')}
              className={DOCK_HEADER_ICON_BUTTON}
              onClick={toggleDock}
            >
              <OpenIcon className='size-3.5' />
            </button>
          </DockPanelActions>
        }
      >
        {isExpanded && (
          <div className='mt-2' data-testid='clearance-card-body'>
            {/* The verdict first, and openable: the rows say which test it
                failed and the footnote quotes the rule, from the same builder
                the dock header uses. */}
            <div className='flex items-center gap-2'>
              <VerdictChip verdict={verdict} rows={rows} footnote={rule} title={rule} />
              <span
                data-testid='clearance-card-reason'
                className='min-w-0 text-[11px] leading-snug text-slate-600 dark:text-stone-300'
              >
                {reason}
              </span>
            </div>

            {violating && (
              // Three EQUAL tiles (`grid-cols-3`, each `min-w-0`) on one
              // baseline — the dock header's figures, in its order.
              <div className='mt-2.5 grid grid-cols-3 gap-2'>
                <DockKpi
                  label={t('clearance.kpi.violations')}
                  value={summary.violationCount.toLocaleString()}
                  testId='clearance-card-violations'
                />
                <DockKpi
                  label={t('clearance.kpi.worst')}
                  value={worst}
                  unit='m'
                  unitTestId='clearance-card-worst-unit'
                  color={
                    summary.maxIntrusionM != null && summary.maxIntrusionM > 0 ? CLEARANCE_FILL.violation : undefined
                  }
                  testId='clearance-card-worst'
                />
                <DockKpi
                  mono
                  label={t('clearance.kpi.worstStation')}
                  value={worstStation}
                  testId='clearance-card-worst-station'
                />
              </div>
            )}

            {violating && summary.worstStationDisplayM != null && (
              // One line: offset and height (the station is the tile above),
              // then the walk. The offset's sign convention is in the hover —
              // a signed offset is unreadable without knowing which side is
              // negative, and a third line of caption under one figure was
              // most of what made the old block heavy.
              <div
                data-testid='clearance-card-worst-point'
                className='mt-2 flex items-baseline justify-between gap-3 text-[11px] leading-4 text-slate-600 dark:text-stone-300'
              >
                <span className='min-w-0 truncate tabular-nums' title={t('stationVolumes.panel.offsetConvention')}>
                  {t('clearance.card.worstLine', {
                    offset: formatSignedOrDash(summary.worstOffsetM, 2),
                    height: formatFixedOrDash(summary.worstHeightM, 2),
                  })}
                </span>
                <button
                  type='button'
                  data-testid='clearance-card-go-worst'
                  title={t('clearance.card.goToWorstHelp')}
                  className='shrink-0 rounded font-medium text-sky-700 underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:text-sky-400'
                  onClick={goToWorst}
                >
                  {t('clearance.card.goToWorst')}
                </button>
              </div>
            )}

            {/* What the run measured against: looked up, not read every
                time, so it is a disclosure and starts closed (audit A7). The
                values WRAP in here — a gauge's citation is the one string
                that has to be quotable in full. */}
            <div className={DOCK_PAD.section}>
              <button
                type='button'
                data-testid='clearance-card-details-toggle'
                aria-expanded={detailsOpen}
                aria-controls={`clearance-card-details-${layerId}`}
                className='flex w-full items-center gap-1 rounded text-[11px] leading-4 font-medium text-slate-500 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:text-stone-400 dark:hover:text-white'
                onClick={() => setDetailsOpen((o) => !o)}
              >
                {detailsOpen ? <ChevronDown className='size-3.5' /> : <ChevronRight className='size-3.5' />}
                {t('clearance.card.runDetails')}
              </button>
              {detailsOpen && (
                <dl
                  id={`clearance-card-details-${layerId}`}
                  data-testid='clearance-card-details'
                  className='mt-1.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[11px] leading-4 text-slate-600 dark:text-stone-300'
                >
                  <dt className='text-slate-500 dark:text-stone-400'>{t('clearance.card.profile')}</dt>
                  <dd className='min-w-0 text-right break-words'>
                    <span className='block text-slate-800 dark:text-stone-100'>{profile.name}</span>
                    {profile.sourceAttribution && (
                      <span
                        data-testid='clearance-card-attribution'
                        className='block text-[10px] leading-snug text-slate-500 dark:text-stone-400'
                      >
                        {profile.sourceAttribution}
                      </span>
                    )}
                  </dd>
                  <dt className='text-slate-500 dark:text-stone-400'>{t('clearance.card.alignment')}</dt>
                  <dd className='min-w-0 text-right break-words'>{alignment.layerName ?? alignment.name}</dd>
                  <dt className='text-slate-500 dark:text-stone-400'>{t('clearance.card.stationInterval')}</dt>
                  <dd className='text-right tabular-nums'>{metres(params.stationIntervalM, 2)}</dd>
                  <dt className='text-slate-500 dark:text-stone-400'>{t('clearance.card.verticalOffset')}</dt>
                  <dd className='text-right tabular-nums'>{signedMetres(params.verticalOffsetM, 2)}</dd>
                </dl>
              )}
            </div>

            {/* The way into the dock, and unmistakably a button: the app's own
                outline Button, an icon that says "panel from below" and a
                verb. It is the ONLY writer of `dataset: 'clearance'`, so the
                test id the old chip carried stays on it. h-7, the dock's one
                control height — slim, but a button at a glance. */}
            <Button
              type='button'
              variant='outline'
              size='sm'
              data-testid='clearance-chip'
              aria-expanded={dockOpen}
              title={dockOpen ? t('clearance.card.hideHelp') : t('clearance.chip.open')}
              className='mt-2 h-7 w-full justify-center gap-1.5 px-2 text-xs font-medium'
              onClick={toggleDock}
            >
              <OpenIcon className='size-3.5' />
              {dockOpen ? t('clearance.card.hide') : t('clearance.card.open')}
            </Button>
          </div>
        )}
      </DockPanel>
    </>
  );
}
